import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const ThemeContext = createContext({
  theme: "light",
  toggleTheme: () => {},
  setTheme: () => {},
  textSize: "m",
  setTextSize: () => {},
  density: "default",
  setDensity: () => {},
});

const THEME_KEY = "aiqa-theme";
const TEXT_KEY = "aiqa-text-size";
const DENSITY_KEY = "aiqa-density";

// Base font size per text-size step, then nudged by density. Because Tailwind's
// spacing scale is rem-based, changing the root font-size scales BOTH type and
// layout — so density genuinely makes the UI tighter / roomier app-wide.
const TEXT_PX = { s: 15, m: 16, l: 18 };
const DENSITY_MULT = { compact: 0.92, default: 1, roomy: 1.08 };

function read(key, allowed, fallback) {
  if (typeof window === "undefined") return fallback;
  try {
    const v = window.localStorage.getItem(key);
    return allowed.includes(v) ? v : fallback;
  } catch (e) {
    return fallback;
  }
}

function getInitialTheme() {
  if (typeof window === "undefined") return "light";
  try {
    const stored = window.localStorage.getItem(THEME_KEY);
    if (stored === "light" || stored === "dark") return stored;
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  } catch (e) {
    return "light";
  }
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(getInitialTheme);
  const [textSize, setTextSize] = useState(() => read(TEXT_KEY, ["s", "m", "l"], "m"));
  const [density, setDensity] = useState(() => read(DENSITY_KEY, ["compact", "default", "roomy"], "default"));

  // Keep the <html> class + localStorage in sync with the theme state.
  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    try {
      window.localStorage.setItem(THEME_KEY, theme);
    } catch (e) {
      /* ignore unavailable storage */
    }
  }, [theme]);

  // Apply text size + density as root font-size (and data-attrs for CSS hooks).
  useEffect(() => {
    const root = document.documentElement;
    const px = (TEXT_PX[textSize] || 16) * (DENSITY_MULT[density] || 1);
    root.style.fontSize = `${px}px`;
    root.dataset.textSize = textSize;
    root.dataset.density = density;
    try {
      window.localStorage.setItem(TEXT_KEY, textSize);
      window.localStorage.setItem(DENSITY_KEY, density);
    } catch (e) {
      /* ignore */
    }
  }, [textSize, density]);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  }, []);

  const value = useMemo(
    () => ({ theme, toggleTheme, setTheme, textSize, setTextSize, density, setDensity }),
    [theme, toggleTheme, textSize, density]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}
